/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, Pause, Square, Clock, Plus, Edit3, Trash2, X, Save, Loader2,
  ChevronDown, ChevronRight, DollarSign, BarChart3, Timer, AlertTriangle,
  CheckCircle, Ruler, TrendingUp, Calendar, Hash
} from "lucide-react";

// ═══════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════

function fmtDuration(totalSeconds) {
  if (!totalSeconds || totalSeconds <= 0) return "0m";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function fmtHours(totalSeconds) {
  return (totalSeconds / 3600).toFixed(1);
}

function fmtTime(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ═══════════════════════════════════════
//  LIVE TIMER HOOK
// ═══════════════════════════════════════

function useLiveElapsed(runningSince) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!runningSince) { setElapsed(0); return; }
    const calc = () => Math.max(0, Math.floor((Date.now() - new Date(runningSince).getTime()) / 1000));
    setElapsed(calc());
    const iv = setInterval(() => setElapsed(calc()), 1000);
    return () => clearInterval(iv);
  }, [runningSince]);
  return elapsed;
}

// ═══════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════

export default function DrawingTracker({ items, onRefresh }) {
  const [summaries, setSummaries] = useState({});
  const [timeRounding, setTimeRounding] = useState("next_30");
  const [activeTimer, setActiveTimer] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("personal");
  const [adding, setAdding] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [confirmSwap, setConfirmSwap] = useState(null);

  const active = items.filter(i => i.status !== "complete");
  const completed = items.filter(i => i.status === "complete");

  const loadSummaries = useCallback(async () => {
    try {
      const [sRes, aRes] = await Promise.all([
        http.get("/marker-collection/admin/drawing-tracker/summaries"),
        http.get("/marker-collection/admin/drawing-tracker/active-timer"),
      ]);
      setSummaries(sRes.data.summaries || {});
      setTimeRounding(sRes.data.time_rounding || "next_30");
      setActiveTimer(aRes.data.running ? aRes.data : null);
    } catch {}
  }, []);

  useEffect(() => { loadSummaries(); }, [loadSummaries]);
  // Refresh summaries every 30s for live timer updates
  useEffect(() => {
    const iv = setInterval(loadSummaries, 30000);
    return () => clearInterval(iv);
  }, [loadSummaries]);

  const addItem = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await http.post("/marker-collection/admin/queue", { title: newTitle.trim(), type: newType });
      toast.success("Added to queue");
      setNewTitle(""); setShowAdd(false); onRefresh(); loadSummaries();
    } catch { toast.error("Failed"); }
    finally { setAdding(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Remove from queue?")) return;
    try {
      await http.delete(`/marker-collection/admin/queue/${id}`);
      toast.success("Removed"); onRefresh(); loadSummaries();
    } catch { toast.error("Failed"); }
  };

  const startTimer = async (itemId, forceStop = false) => {
    try {
      await http.post(`/marker-collection/admin/drawing-tracker/${itemId}/start`, { force_stop_other: forceStop });
      toast.success("Timer started");
      loadSummaries(); onRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail) {
        setConfirmSwap({ targetId: itemId, activeTitle: detail.active_queue_item_title, activeId: detail.active_queue_item_id });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to start"));
      }
    }
  };

  const stopTimer = async (itemId) => {
    try {
      await http.post(`/marker-collection/admin/drawing-tracker/${itemId}/stop`);
      toast.success("Timer stopped");
      loadSummaries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="drawing-tracker">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase">
            {active.length} active · {completed.length} completed
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAnalytics(true)}
            className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5 border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
            data-testid="dt-analytics-btn">
            <BarChart3 size={12} /> Analytics
          </button>
          <button onClick={() => setShowSettings(true)}
            className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5"
            data-testid="dt-settings-btn">
            <DollarSign size={12} /> Pricing Settings
          </button>
          <button onClick={() => setShowAdd(!showAdd)}
            className="bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a]"
            data-testid="dt-add-btn">
            <Plus size={14} /> Add Project
          </button>
        </div>
      </div>

      {/* ─── Add Form ─── */}
      {showAdd && (
        <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-4 space-y-3" data-testid="dt-add-form">
          <input className="stc-input w-full font-mono text-[12px]" placeholder="Project title..."
            value={newTitle} onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addItem()} data-testid="dt-add-title" />
          <div className="flex items-center gap-3">
            <select className="stc-input text-[12px]" value={newType} onChange={e => setNewType(e.target.value)} data-testid="dt-add-type">
              <option value="personal">Personal Project</option>
              <option value="commission">Commission</option>
            </select>
            <button onClick={addItem} disabled={adding || !newTitle.trim()}
              className="bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase hover:bg-[#b8915a] disabled:opacity-50"
              data-testid="dt-add-submit">
              {adding ? <Loader2 size={14} className="animate-spin" /> : "Add"}
            </button>
            <button onClick={() => setShowAdd(false)} className="text-gray-500 hover:text-white font-ui text-[10px]">Cancel</button>
          </div>
        </div>
      )}

      {/* ─── Active Timer Banner ─── */}
      {activeTimer && (
        <ActiveTimerBanner timer={activeTimer} onStop={() => stopTimer(activeTimer.queue_item_id)} />
      )}

      {/* ─── Queue Rows ─── */}
      {active.length === 0 ? (
        <div className="border-2 border-dashed border-white/10 text-center py-12">
          <Clock size={24} className="mx-auto mb-3 text-[#8a857d]" />
          <p className="text-[#8a857d] text-sm">Queue is empty. Add a project to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {active.map(item => (
            <QueueRow key={item.id} item={item} summary={summaries[item.id]}
              isTimerRunning={activeTimer?.queue_item_id === item.id}
              onStart={() => startTimer(item.id)}
              onStop={() => stopTimer(item.id)}
              onDetail={() => setDetailItem(item)}
              onDelete={() => deleteItem(item.id)}
              onRefresh={() => { onRefresh(); loadSummaries(); }} />
          ))}
        </div>
      )}

      {/* ─── Completed ─── */}
      {completed.length > 0 && (
        <div className="mt-6">
          <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] uppercase mb-2">Completed ({completed.length})</div>
          <div className="space-y-1">
            {completed.map(item => (
              <div key={item.id} className="border border-white/5 bg-[#0a0a0a]/50 px-4 py-2.5 flex items-center gap-3"
                data-testid={`dt-completed-${item.id}`}>
                <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" />
                <span className="text-[#8a857d] text-xs flex-1 truncate">{item.title}</span>
                <span className="text-[9px] text-[#6a6660] font-mono">{fmtDuration(summaries[item.id]?.total_seconds || 0)}</span>
                <button onClick={() => setDetailItem(item)} className="text-[#8a857d] hover:text-[#C9A055]" title="Details"><ChevronRight size={14} /></button>
                <button onClick={() => deleteItem(item.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Modals ─── */}
      {detailItem && (
        <ProjectDetail item={detailItem} summary={summaries[detailItem.id]}
          isTimerRunning={activeTimer?.queue_item_id === detailItem.id}
          onStart={() => startTimer(detailItem.id)}
          onStop={() => stopTimer(detailItem.id)}
          onClose={() => { setDetailItem(null); loadSummaries(); onRefresh(); }}
          onRefresh={() => { onRefresh(); loadSummaries(); }} />
      )}

      {showAnalytics && <AnalyticsModal onClose={() => setShowAnalytics(false)} />}
      {showSettings && <PricingSettingsModal onClose={() => { setShowSettings(false); loadSummaries(); }} />}

      {/* ─── Swap Confirm ─── */}
      {confirmSwap && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setConfirmSwap(null)}>
          <div className="bg-[#1a1816] border border-amber-500/30 max-w-sm w-full p-6" onClick={e => e.stopPropagation()} data-testid="dt-swap-confirm">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-amber-400" />
              <h3 className="font-ui text-[11px] tracking-[0.2em] uppercase text-amber-400">Timer Already Running</h3>
            </div>
            <p className="text-sm text-[#B5B0A7] mb-4">
              <strong className="text-[#F5F2EB]">"{confirmSwap.activeTitle}"</strong> has an active timer.
              Stop it and start the new timer?
            </p>
            <div className="flex gap-2">
              <button onClick={() => { startTimer(confirmSwap.targetId, true); setConfirmSwap(null); }}
                className="bg-amber-600 text-black px-4 py-2 font-ui text-[10px] tracking-[0.2em] uppercase hover:bg-amber-500"
                data-testid="dt-swap-yes">Stop & Start New</button>
              <button onClick={() => setConfirmSwap(null)}
                className="btn-outline text-[10px] px-4 py-2">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
//  ACTIVE TIMER BANNER
// ═══════════════════════════════════════

function ActiveTimerBanner({ timer, onStop }) {
  const elapsed = useLiveElapsed(timer.started_at);
  return (
    <div className="mb-4 border border-emerald-500/30 bg-emerald-900/10 px-4 py-3 flex items-center gap-3 animate-pulse-slow"
      data-testid="dt-active-banner">
      <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-emerald-300 truncate">{timer.queue_item_title}</p>
        <p className="text-[10px] text-emerald-500/70">Started {fmtTime(timer.started_at)}</p>
      </div>
      <span className="font-mono text-lg font-bold text-emerald-300" data-testid="dt-active-elapsed">{fmtDuration(elapsed)}</span>
      <button onClick={onStop}
        className="bg-red-600/80 text-white px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase flex items-center gap-1 hover:bg-red-500"
        data-testid="dt-active-stop">
        <Square size={10} /> Stop
      </button>
    </div>
  );
}

// ═══════════════════════════════════════
//  QUEUE ROW
// ═══════════════════════════════════════

function QueueRow({ item, summary, isTimerRunning, onStart, onStop, onDetail, onDelete, onRefresh }) {
  const runningSince = summary?.running ? summary.running_since : null;
  const liveElapsed = useLiveElapsed(runningSince);
  const totalSeconds = (summary?.total_seconds || 0) + (isTimerRunning ? liveElapsed - (summary?.total_seconds || 0) : 0);
  const displaySeconds = isTimerRunning ? (summary?.total_seconds || 0) : (summary?.total_seconds || 0);
  const pricing = item.pricing || {};
  const size = pricing.size || "";
  const finalPrice = pricing.final_selling_price;

  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editStatus, setEditStatus] = useState(item.status);

  const saveEdit = async () => {
    try {
      const patch = {};
      if (editTitle.trim() !== item.title) patch.title = editTitle.trim();
      if (editStatus !== item.status) patch.status = editStatus;
      if (Object.keys(patch).length) {
        await http.patch(`/marker-collection/admin/queue/${item.id}`, patch);
        toast.success("Updated"); onRefresh();
      }
      setEditMode(false);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className={`border px-3 sm:px-4 py-3 ${isTimerRunning ? "border-emerald-500/40 bg-emerald-900/5" : "border-white/8 bg-[#0a0a0a]"}`}
      data-testid={`dt-row-${item.id}`}>

      {editMode ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input className="stc-input text-[12px] flex-1 min-w-[120px]" value={editTitle}
            onChange={e => setEditTitle(e.target.value)} data-testid="dt-edit-title" />
          <select className="stc-input text-[10px]" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
            <option value="queued">Queued</option>
            <option value="in_progress">In Progress</option>
            <option value="complete">Complete</option>
          </select>
          <button onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300"><CheckCircle size={14} /></button>
          <button onClick={() => setEditMode(false)} className="text-gray-500 hover:text-white"><X size={14} /></button>
        </div>
      ) : (
        <>
          {/* Row 1: Play + Title + Time */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {isTimerRunning ? (
                <button onClick={onStop}
                  className="w-10 h-10 flex items-center justify-center bg-red-600/80 hover:bg-red-500 text-white transition-colors"
                  data-testid={`dt-stop-${item.id}`}>
                  <Square size={14} />
                </button>
              ) : (
                <button onClick={onStart}
                  className="w-10 h-10 flex items-center justify-center bg-emerald-600/80 hover:bg-emerald-500 text-white transition-colors"
                  data-testid={`dt-start-${item.id}`}>
                  <Play size={14} />
                </button>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#F5F2EB] truncate">{item.title}</span>
                {isTimerRunning && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />}
              </div>
            </div>

            <div className="text-right flex-shrink-0">
              {isTimerRunning ? (
                <span className="font-mono text-sm font-bold text-emerald-300" data-testid={`dt-time-${item.id}`}>
                  {fmtDuration((summary?.total_seconds || 0))}
                </span>
              ) : (
                <span className="font-mono text-sm text-[#8a857d]" data-testid={`dt-time-${item.id}`}>
                  {fmtDuration(summary?.total_seconds || 0)}
                </span>
              )}
              {isTimerRunning && <LiveCounter since={summary?.running_since} />}
            </div>
          </div>

          {/* Row 2: Badges + Price + Actions */}
          <div className="flex items-center gap-2 mt-2 ml-[52px]">
            <span className={`text-[8px] tracking-[0.15em] uppercase px-1.5 py-0.5 ${
              item.type === "commission" ? "text-[#C9A055] bg-[#C9A055]/10" : "text-[#8a857d] bg-white/5"
            }`}>{item.type}</span>
            <span className={`text-[8px] tracking-[0.15em] uppercase px-1.5 py-0.5 ${
              item.status === "in_progress" ? "text-amber-400 bg-amber-900/20" : "text-[#8a857d] bg-white/5"
            }`}>{item.status === "in_progress" ? "IN PROGRESS" : item.status}</span>
            {size && <span className="text-[8px] text-[#6a6660] flex items-center gap-0.5"><Ruler size={8} />{size}</span>}

            <div className="flex-1" />

            {finalPrice != null && (
              <span className="font-mono text-xs font-bold text-[#C9A055]">${finalPrice}</span>
            )}

            <div className="flex gap-0.5 flex-shrink-0">
              <button onClick={onDetail} className="p-1.5 text-[#8a857d] hover:text-[#C9A055]" title="Details"
                data-testid={`dt-detail-${item.id}`}><ChevronRight size={14} /></button>
              <button onClick={() => { setEditMode(true); setEditTitle(item.title); setEditStatus(item.status); }}
                className="p-1.5 text-[#8a857d] hover:text-white" title="Edit"
                data-testid={`dt-edit-${item.id}`}><Edit3 size={12} /></button>
              <button onClick={onDelete} className="p-1.5 text-gray-600 hover:text-red-400" title="Delete"
                data-testid={`dt-del-${item.id}`}><Trash2 size={12} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LiveCounter({ since }) {
  const elapsed = useLiveElapsed(since);
  return <p className="text-[9px] text-emerald-500 font-mono animate-pulse">+{fmtDuration(elapsed)}</p>;
}

// ═══════════════════════════════════════
//  PROJECT DETAIL MODAL
// ═══════════════════════════════════════

function ProjectDetail({ item, summary, isTimerRunning, onStart, onStop, onClose, onRefresh }) {
  const [sessions, setSessions] = useState([]);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [addMin, setAddMin] = useState("");
  const [addNote, setAddNote] = useState("");
  const [showAddTime, setShowAddTime] = useState(false);
  const pricing = item.pricing || {};
  const [size, setSize] = useState(pricing.size || "");
  const [materials, setMaterials] = useState(pricing.materials_cost || "");
  const [complexity, setComplexity] = useState(pricing.complexity_adjustment || "");
  const [commPremium, setCommPremium] = useState(pricing.commission_premium || "");
  const [manualAdj, setManualAdj] = useState(pricing.manual_price_adjustment || "");
  const [minPrice, setMinPrice] = useState(pricing.minimum_price || "");
  const [finalPrice, setFinalPrice] = useState(pricing.final_selling_price ?? "");
  const [actualSold, setActualSold] = useState(pricing.actual_selling_price ?? "");
  const [rateOverride, setRateOverride] = useState(pricing.hourly_rate_override || "");
  const [baseOverride, setBaseOverride] = useState(pricing.base_price_override || "");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, cRes, stRes] = await Promise.all([
        http.get(`/marker-collection/admin/drawing-tracker/${item.id}/sessions`),
        http.get(`/marker-collection/admin/drawing-tracker/${item.id}/calculate`),
        http.get("/marker-collection/admin/drawing-tracker/settings"),
      ]);
      setSessions(sRes.data || []);
      setCalc(cRes.data);
      setSettings(stRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [item.id]);

  useEffect(() => { load(); }, [load]);

  const savePricing = async () => {
    setSaving(true);
    try {
      const data = {
        size: size || null,
        materials_cost: parseFloat(materials) || 0,
        complexity_adjustment: parseFloat(complexity) || 0,
        commission_premium: parseFloat(commPremium) || 0,
        manual_price_adjustment: parseFloat(manualAdj) || 0,
        minimum_price: parseFloat(minPrice) || 0,
        hourly_rate_override: parseFloat(rateOverride) || null,
        base_price_override: parseFloat(baseOverride) || null,
      };
      if (finalPrice !== "" && finalPrice !== null) data.final_selling_price = parseFloat(finalPrice) || 0;
      if (actualSold !== "" && actualSold !== null) data.actual_selling_price = parseFloat(actualSold) || 0;
      await http.patch(`/marker-collection/admin/drawing-tracker/${item.id}/pricing`, data);
      toast.success("Pricing saved");
      load(); onRefresh();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addManualTime = async () => {
    const min = parseFloat(addMin);
    if (!min) { toast.error("Enter minutes"); return; }
    try {
      await http.post(`/marker-collection/admin/drawing-tracker/${item.id}/manual-session`, {
        minutes: min, note: addNote || (min > 0 ? "Manual time added" : "Time adjustment"),
      });
      toast.success(min > 0 ? "Time added" : "Time adjusted");
      setAddMin(""); setAddNote(""); setShowAddTime(false); load();
    } catch { toast.error("Failed"); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await http.delete(`/marker-collection/admin/drawing-tracker/sessions/${sid}`);
      toast.success("Session deleted"); load();
    } catch { toast.error("Failed"); }
  };

  const sizes = settings?.sizes || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#141210] border border-white/10 max-w-3xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="dt-detail-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#141210] z-10">
          <div className="flex items-center gap-3">
            <Timer size={16} className="text-[#C9A055]" />
            <span className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#C9A055]">{item.title}</span>
          </div>
          <div className="flex items-center gap-2">
            {isTimerRunning ? (
              <button onClick={onStop} className="bg-red-600/80 text-white px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase flex items-center gap-1 hover:bg-red-500"
                data-testid="dt-modal-stop"><Square size={10} /> Stop</button>
            ) : (
              <button onClick={onStart} className="bg-emerald-600/80 text-white px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase flex items-center gap-1 hover:bg-emerald-500"
                data-testid="dt-modal-start"><Play size={10} /> Start</button>
            )}
            <button onClick={onClose} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#C9A055]" /></div>
        ) : (
          <div className="p-5 space-y-6">
            {/* ─── Time Summary ─── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox label="Actual Time" value={calc ? `${fmtHours(calc.actual_minutes * 60)} hrs` : "—"} accent />
              <StatBox label="Billable Time" value={calc ? `${calc.billable_hours} hrs` : "—"} />
              <StatBox label="Sessions" value={calc?.session_count ?? 0} />
              <StatBox label="Rounding" value={(settings?.time_rounding || "next_30").replace("_", " ")} />
            </div>

            {/* ─── Time Log ─── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] uppercase">Time Log</span>
                <button onClick={() => setShowAddTime(!showAddTime)}
                  className="font-ui text-[9px] tracking-[0.15em] uppercase text-[#C9A055] hover:text-[#b8915a] flex items-center gap-1"
                  data-testid="dt-add-time-btn">
                  <Plus size={10} /> Add Time
                </button>
              </div>

              {showAddTime && (
                <div className="border border-[#C9A055]/20 bg-[#C9A055]/5 p-3 mb-3 space-y-2" data-testid="dt-add-time-form">
                  <div className="flex gap-2">
                    <input type="number" className="stc-input text-[12px] w-24 font-mono" value={addMin}
                      onChange={e => setAddMin(e.target.value)} placeholder="±min" data-testid="dt-add-time-min" />
                    <input className="stc-input text-[12px] flex-1" value={addNote}
                      onChange={e => setAddNote(e.target.value)} placeholder="Note (e.g., Worked on sketch away from computer)"
                      data-testid="dt-add-time-note" />
                    <button onClick={addManualTime} className="bg-[#C9A055] text-black px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase"
                      data-testid="dt-add-time-save">Add</button>
                  </div>
                  <p className="text-[8px] text-[#8a857d]">Positive = add time. Negative = subtract time.</p>
                </div>
              )}

              <div className="border border-white/5 divide-y divide-white/5 max-h-[220px] overflow-y-auto">
                {sessions.length === 0 ? (
                  <p className="text-[10px] text-[#6a6660] text-center py-4">No sessions yet</p>
                ) : sessions.map(s => (
                  <div key={s.id} className="flex items-center gap-3 px-3 py-2 group" data-testid={`dt-session-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      {s.type === "manual" ? (
                        <div>
                          <span className="text-[10px] text-amber-400">Manual Adjustment</span>
                          {s.note && <span className="text-[9px] text-[#6a6660] ml-2">"{s.note}"</span>}
                        </div>
                      ) : (
                        <div>
                          <span className="text-[10px] text-[#B5B0A7]">{fmtDate(s.started_at)}</span>
                          <span className="text-[10px] text-[#F5F2EB] ml-2">
                            {fmtTime(s.started_at)} – {s.status === "running" ? <span className="text-emerald-400">Running</span> : fmtTime(s.stopped_at)}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className={`font-mono text-[11px] font-bold ${s.elapsed_seconds < 0 ? "text-red-400" : s.type === "manual" ? "text-amber-400" : "text-[#F5F2EB]"}`}>
                      {s.elapsed_seconds < 0 ? "-" : s.type === "manual" ? "+" : ""}{fmtDuration(Math.abs(s.elapsed_seconds))}
                    </span>
                    <button onClick={() => deleteSession(s.id)}
                      className="p-0.5 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ─── Pricing ─── */}
            <div>
              <span className="font-ui text-[9px] tracking-[0.3em] text-[#C9A055] uppercase block mb-3">Pricing Calculator</span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">SIZE</label>
                  <select className="stc-input w-full text-[11px]" value={size} onChange={e => setSize(e.target.value)} data-testid="dt-size">
                    <option value="">Select size...</option>
                    {sizes.map(s => <option key={s.label} value={s.label}>{s.label} (${s.base_price})</option>)}
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">BASE PRICE OVERRIDE</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={baseOverride}
                    onChange={e => setBaseOverride(e.target.value)} placeholder="Auto" data-testid="dt-base-override" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">HOURLY RATE OVERRIDE</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={rateOverride}
                    onChange={e => setRateOverride(e.target.value)} placeholder="Auto" data-testid="dt-rate-override" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">MATERIALS / FRAMING</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={materials}
                    onChange={e => setMaterials(e.target.value)} placeholder="$0" data-testid="dt-materials" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">COMPLEXITY ADJ.</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={complexity}
                    onChange={e => setComplexity(e.target.value)} placeholder="$0" data-testid="dt-complexity" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">COMMISSION PREMIUM</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={commPremium}
                    onChange={e => setCommPremium(e.target.value)} placeholder="$0" data-testid="dt-commission" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">MANUAL ADJUSTMENT</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={manualAdj}
                    onChange={e => setManualAdj(e.target.value)} placeholder="$0" data-testid="dt-manual-adj" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">MINIMUM PRICE</label>
                  <input type="number" className="stc-input w-full text-[11px] font-mono" value={minPrice}
                    onChange={e => setMinPrice(e.target.value)} placeholder="$0" data-testid="dt-min-price" />
                </div>
              </div>

              {/* Price Breakdown */}
              {calc && (
                <div className="mt-4 border border-white/10 bg-[#0d0c0a] p-4" data-testid="dt-price-breakdown">
                  <div className="space-y-1.5 text-[11px]">
                    <Row label="Base Price" value={`$${calc.breakdown.base_price}`} />
                    <Row label={`Labor: ${calc.billable_hours} hrs × $${calc.breakdown.hourly_rate}/hr`} value={`$${calc.breakdown.labor}`} />
                    {calc.breakdown.materials > 0 && <Row label="Materials / Framing" value={`$${calc.breakdown.materials}`} />}
                    {calc.breakdown.complexity > 0 && <Row label="Complexity" value={`+$${calc.breakdown.complexity}`} />}
                    {calc.breakdown.commission_premium > 0 && <Row label="Commission Premium" value={`+$${calc.breakdown.commission_premium}`} />}
                    {calc.breakdown.manual_adjustment !== 0 && <Row label="Manual Adjustment" value={`${calc.breakdown.manual_adjustment > 0 ? "+" : ""}$${calc.breakdown.manual_adjustment}`} />}
                    <div className="border-t border-white/10 pt-1.5 mt-1.5" />
                    <Row label="Calculated Price" value={`$${calc.calculated_price}`} bold />
                    <Row label={`Suggested (rounded ${calc.price_rounding.replace("_", " ")})`} value={`$${calc.suggested_rounded_price}`} accent />
                  </div>
                </div>
              )}

              {/* Final Prices */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-[#C9A055] block mb-1">FINAL SELLING PRICE</label>
                  <input type="number" className="stc-input w-full text-[14px] font-mono font-bold" value={finalPrice}
                    onChange={e => setFinalPrice(e.target.value)} placeholder="Enter price..."
                    data-testid="dt-final-price" />
                </div>
                <div>
                  <label className="font-ui text-[8px] tracking-[0.3em] text-emerald-500 block mb-1">ACTUAL SELLING PRICE (if sold)</label>
                  <input type="number" className="stc-input w-full text-[14px] font-mono font-bold" value={actualSold}
                    onChange={e => setActualSold(e.target.value)} placeholder="—"
                    data-testid="dt-actual-sold" />
                </div>
              </div>

              {/* Profit Stats */}
              {calc && calc.profit != null && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <StatBox label="Profit" value={`$${calc.profit}`} accent />
                  <StatBox label="Effective $/hr" value={calc.effective_hourly != null ? `$${calc.effective_hourly}` : "—"} />
                </div>
              )}

              <button onClick={savePricing} disabled={saving}
                className="mt-4 bg-[#C9A055] text-black px-6 py-2.5 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] disabled:opacity-50"
                data-testid="dt-save-pricing">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Pricing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="border border-white/5 bg-[#0a0a0a] p-3">
      <p className="text-[8px] tracking-[0.2em] uppercase text-[#6a6660] mb-0.5">{label}</p>
      <p className={`text-sm font-bold font-mono ${accent ? "text-[#C9A055]" : "text-[#F5F2EB]"}`}>{value}</p>
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`${bold ? "font-bold text-[#F5F2EB]" : "text-[#8a857d]"}`}>{label}</span>
      <span className={`font-mono font-bold ${accent ? "text-[#C9A055] text-sm" : bold ? "text-[#F5F2EB]" : "text-[#B5B0A7]"}`}>{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════
//  ANALYTICS MODAL
// ═══════════════════════════════════════

function AnalyticsModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await http.get(`/marker-collection/admin/drawing-tracker/analytics?days=${period}`);
      setData(d);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#141210] border border-white/10 max-w-3xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="dt-analytics-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-purple-400" />
            <span className="font-ui text-[10px] tracking-[0.2em] uppercase text-purple-400">Drawing Analytics</span>
          </div>
          <button onClick={onClose} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
        </div>

        {/* Period Filter */}
        <div className="px-5 pt-4 flex gap-2" data-testid="dt-analytics-filters">
          {[
            [0, "All Time"], [30, "Last 30 Days"], [90, "Last 90 Days"], [365, "This Year"]
          ].map(([days, label]) => (
            <button key={days} onClick={() => setPeriod(days)}
              className={`px-3 py-1.5 text-[9px] tracking-[0.15em] uppercase border transition-colors ${
                period === days ? "bg-purple-600/30 text-purple-200 border-purple-500/50" : "text-[#8a857d] border-white/10 hover:border-purple-500/30"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-purple-400" /></div>
        ) : !data ? (
          <p className="text-center py-12 text-[#8a857d]">No data</p>
        ) : (
          <div className="p-5 space-y-5">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox label="Completed" value={data.total_completed} />
              <StatBox label="Total Hours" value={`${data.total_hours}h`} />
              <StatBox label="Total Sales" value={`$${data.total_sales}`} accent />
              <StatBox label="Total Profit" value={`$${data.total_profit}`} accent />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox label="Avg Hours/Drawing" value={`${data.avg_hours_per_drawing}h`} />
              <StatBox label="Avg Selling Price" value={`$${data.avg_selling_price}`} />
              <StatBox label="Avg $/hr" value={`$${data.avg_effective_hourly}`} accent />
              <StatBox label="Avg Profit" value={`$${data.avg_profit_per_original}`} />
            </div>

            {/* Most Profitable Sizes */}
            {data.most_profitable_sizes?.length > 0 && (
              <div>
                <span className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] uppercase block mb-2">Most Profitable Sizes</span>
                <div className="flex gap-2 flex-wrap">
                  {data.most_profitable_sizes.map((s, i) => (
                    <span key={s} className="px-3 py-1.5 text-[10px] font-bold border bg-[#C9A055]/5 border-[#C9A055]/20 text-[#C9A055]">
                      #{i + 1} {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Size Breakdown */}
            {Object.keys(data.size_breakdown || {}).length > 0 && (
              <div>
                <span className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] uppercase block mb-2">By Size</span>
                <div className="border border-white/5 overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-[#8a857d] border-b border-white/5">
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">Avg Hrs</th>
                        <th className="px-3 py-2 text-right">Avg Price</th>
                        <th className="px-3 py-2 text-right">Avg Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.size_breakdown).map(([sz, stats]) => (
                        <tr key={sz} className="border-b border-white/3 text-[#B5B0A7]">
                          <td className="px-3 py-2 font-bold text-[#F5F2EB]">{sz}</td>
                          <td className="px-3 py-2 text-right">{stats.count}</td>
                          <td className="px-3 py-2 text-right font-mono">{stats.avg_hours}h</td>
                          <td className="px-3 py-2 text-right font-mono">${stats.avg_selling}</td>
                          <td className="px-3 py-2 text-right font-mono text-[#C9A055]">${stats.avg_profit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.total_completed === 0 && (
              <p className="text-center text-[#6a6660] py-4 text-sm">Complete some drawings to see analytics!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  PRICING SETTINGS MODAL
// ═══════════════════════════════════════

function PricingSettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSizeLabel, setNewSizeLabel] = useState("");
  const [newSizeBase, setNewSizeBase] = useState("");
  const [newSizeRate, setNewSizeRate] = useState("");

  useEffect(() => {
    http.get("/marker-collection/admin/drawing-tracker/settings").then(r => {
      setSettings(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await http.patch("/marker-collection/admin/drawing-tracker/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const addSize = () => {
    if (!newSizeLabel.trim()) return;
    const updated = [...(settings.sizes || []), {
      label: newSizeLabel.trim(),
      width: 0, height: 0,
      base_price: parseFloat(newSizeBase) || 0,
      hourly_rate: parseFloat(newSizeRate) || settings.default_hourly_rate || 35,
    }];
    setSettings(s => ({ ...s, sizes: updated }));
    setNewSizeLabel(""); setNewSizeBase(""); setNewSizeRate("");
  };

  const removeSize = (idx) => {
    setSettings(s => ({ ...s, sizes: s.sizes.filter((_, i) => i !== idx) }));
  };

  const updateSize = (idx, field, value) => {
    setSettings(s => {
      const sizes = [...s.sizes];
      sizes[idx] = { ...sizes[idx], [field]: field === "label" ? value : parseFloat(value) || 0 };
      return { ...s, sizes };
    });
  };

  if (loading || !settings) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <Loader2 size={24} className="animate-spin text-[#C9A055]" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#141210] border border-white/10 max-w-2xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="dt-settings-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <span className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#C9A055]">Pricing Settings</span>
          <button onClick={onClose} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Rounding */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">TIME ROUNDING</label>
              <select className="stc-input w-full text-[11px]" value={settings.time_rounding}
                onChange={e => setSettings(s => ({ ...s, time_rounding: e.target.value }))} data-testid="dt-set-time-round">
                <option value="exact">Exact Time</option>
                <option value="next_15">Next 15 Minutes</option>
                <option value="next_30">Next 30 Minutes</option>
                <option value="next_60">Next Full Hour</option>
              </select>
            </div>
            <div>
              <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">PRICE ROUNDING</label>
              <select className="stc-input w-full text-[11px]" value={settings.price_rounding}
                onChange={e => setSettings(s => ({ ...s, price_rounding: e.target.value }))} data-testid="dt-set-price-round">
                <option value="next_5">Next $5</option>
                <option value="next_10">Next $10</option>
                <option value="next_25">Next $25</option>
                <option value="next_50">Next $50</option>
                <option value="next_100">Next $100</option>
              </select>
            </div>
          </div>

          {/* Default Rate */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">DEFAULT HOURLY RATE</label>
              <input type="number" className="stc-input w-full text-[11px] font-mono" value={settings.default_hourly_rate}
                onChange={e => setSettings(s => ({ ...s, default_hourly_rate: parseFloat(e.target.value) || 0 }))}
                data-testid="dt-set-default-rate" />
            </div>
            <div>
              <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">DEFAULT MATERIALS</label>
              <input type="number" className="stc-input w-full text-[11px] font-mono" value={settings.default_materials}
                onChange={e => setSettings(s => ({ ...s, default_materials: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <label className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] block mb-1">DEFAULT COMPLEXITY</label>
              <input type="number" className="stc-input w-full text-[11px] font-mono" value={settings.default_complexity}
                onChange={e => setSettings(s => ({ ...s, default_complexity: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>

          {/* Sizes */}
          <div>
            <span className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] uppercase block mb-2">Size Pricing ({settings.sizes?.length})</span>
            <div className="border border-white/5 overflow-x-auto max-h-[280px] overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-[#8a857d] border-b border-white/5 sticky top-0 bg-[#141210]">
                    <th className="px-2 py-2 text-left">Size</th>
                    <th className="px-2 py-2 text-right">Base Price</th>
                    <th className="px-2 py-2 text-right">Hourly Rate</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(settings.sizes || []).map((s, i) => (
                    <tr key={i} className="border-b border-white/3">
                      <td className="px-2 py-1.5">
                        <input className="stc-input text-[11px] w-full" value={s.label}
                          onChange={e => updateSize(i, "label", e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" className="stc-input text-[11px] w-full font-mono text-right" value={s.base_price}
                          onChange={e => updateSize(i, "base_price", e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" className="stc-input text-[11px] w-full font-mono text-right" value={s.hourly_rate}
                          onChange={e => updateSize(i, "hourly_rate", e.target.value)} />
                      </td>
                      <td className="px-1 py-1.5 text-center">
                        <button onClick={() => removeSize(i)} className="text-gray-600 hover:text-red-400"><X size={10} /></button>
                      </td>
                    </tr>
                  ))}
                  {/* Add row */}
                  <tr className="border-t border-[#C9A055]/20">
                    <td className="px-2 py-1.5">
                      <input className="stc-input text-[11px] w-full" value={newSizeLabel}
                        onChange={e => setNewSizeLabel(e.target.value)} placeholder="e.g., 30x40" data-testid="dt-set-new-size-label" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" className="stc-input text-[11px] w-full font-mono text-right" value={newSizeBase}
                        onChange={e => setNewSizeBase(e.target.value)} placeholder="$0" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" className="stc-input text-[11px] w-full font-mono text-right" value={newSizeRate}
                        onChange={e => setNewSizeRate(e.target.value)} placeholder="$35" />
                    </td>
                    <td className="px-1 py-1.5 text-center">
                      <button onClick={addSize} className="text-[#C9A055] hover:text-[#b8915a]" data-testid="dt-set-add-size"><Plus size={12} /></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <button onClick={save} disabled={saving}
            className="bg-[#C9A055] text-black px-6 py-2.5 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] disabled:opacity-50"
            data-testid="dt-settings-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
